import { useEffect, useMemo } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import {
  useAuthContext,
  useCartActions,
  useClientConfig,
  useCustomerContext,
  useOfflineModeContext,
} from '../context';
import { PRICE_DISPLAY_MODES } from '../config/productPricing';
import { getPriceInquiryPatch } from '../utils/productPriceInquiry';
import useProductPriceInquiry from './useProductPriceInquiry';

const normalizeValue = (value) => `${value ?? ''}`.trim().toUpperCase();

const getTariffGroup = (...items) => {
  for (const item of items) {
    const tariffGroup = item?.tarifnaGrupa ?? item?.tarifnagrupa;
    if (tariffGroup !== undefined && tariffGroup !== null && tariffGroup !== '') {
      return tariffGroup;
    }
  }
  return undefined;
};

const matchesOrganizationPricingRule = (rule, organization) => {
  if (!rule || !organization) return false;
  const organizationId = organization?.id ?? organization;
  const organizationName = organization?.name ?? (typeof organization === 'string' ? organization : '');
  const organizationIds = (rule.organizationIds || []).map((id) => `${id}`);
  const organizationNames = (rule.organizationNames || []).map(normalizeValue).filter(Boolean);
  return (
    organizationIds.includes(`${organizationId}`) ||
    organizationNames.some((name) => normalizeValue(organizationName).includes(name))
  );
};

export default function useProductEntryPricing(product, { selected, inCart, updateCartPricing = true }) {
  const client = useApolloClient();
  const { authUser } = useAuthContext();
  const { customer } = useCustomerContext();
  const { networkState } = useOfflineModeContext();
  const { updateCartItem, organization } = useCartActions();
  const { getValue } = useClientConfig();
  const usesPriceInquiry = getValue('productPricing.usePriceInquiry', false);
  const { price: inquiryPrice, load: fetchInquiryPrice, revision } = useProductPriceInquiry({
    client,
    userId: authUser?.id,
    customerId: customer?.id,
    organizationId: organization?.id ?? organization ?? authUser?.organization?.id,
    productId: product.id,
    enabled: usesPriceInquiry,
    retry: getValue('productPricing.retryPriceInquiry', false) && usesPriceInquiry && (selected || inCart),
    priority: selected,
    offline: !networkState?.online,
    timeoutMs: getValue('productPricing.inquiryTimeoutMs', 12000),
    onPrice: (quote) => {
      if (!updateCartPricing) return;
      const tariffGroup = getTariffGroup(quote, product);
      updateCartItem({
        id: product.id,
        ...getPriceInquiryPatch(quote),
        tarifnaGrupa: tariffGroup,
        tarifnagrupa: tariffGroup,
      });
    },
  });

  const priceDisplayMode = useMemo(() => {
    const productPricing = getValue('productPricing', {});
    const matchedOverride = (productPricing.organizationOverrides || []).find((rule) =>
      [organization, authUser?.organization].some((item) => matchesOrganizationPricingRule(rule, item)),
    );
    return matchedOverride?.displayMode || productPricing.defaultDisplayMode || PRICE_DISPLAY_MODES.STANDARD;
  }, [getValue, organization, authUser?.organization]);
  const shouldUseRetailPdvPrice = priceDisplayMode === PRICE_DISPLAY_MODES.RETAIL_WITH_PDV;
  const shouldUseWholesaleWithoutPdvPrice = priceDisplayMode === PRICE_DISPLAY_MODES.WHOLESALE_WITHOUT_PDV;

  const effectiveProduct = useMemo(() => {
    const tariffGroup = getTariffGroup(inquiryPrice, product);
    if (!inquiryPrice) {
      if (shouldUseRetailPdvPrice) {
        return {
          ...product,
          wholesalePrice: product.retailPrice || product.wholesalePrice,
          price: product.retailPrice || product.price,
        };
      }
      return product;
    }
    return {
      ...product,
      ...inquiryPrice,
      wholesalePrice: shouldUseWholesaleWithoutPdvPrice
        ? (inquiryPrice.cenaCenovnikBezPDV ?? inquiryPrice.nettoVpc ?? product.wholesalePrice)
        : shouldUseRetailPdvPrice
          ? (inquiryPrice.krajnjaCenaSaPDV ?? inquiryPrice.price ?? product.wholesalePrice)
          : (inquiryPrice.price ?? product.wholesalePrice),
      price: shouldUseWholesaleWithoutPdvPrice
        ? (inquiryPrice.cenaCenovnikBezPDV ?? inquiryPrice.nettoVpc ?? product.price)
        : shouldUseRetailPdvPrice
          ? (inquiryPrice.krajnjaCenaSaPDV ?? inquiryPrice.price ?? product.price)
          : (inquiryPrice.price ?? product.price),
      discount1: inquiryPrice.discount1 ?? product.discount1,
      discount2: inquiryPrice.discount2 ?? product.discount2,
      discount3: inquiryPrice.discount3 ?? 0,
      nettoVpc: inquiryPrice.nettoVpc,
      cenaCenovnikBezPDV: inquiryPrice.cenaCenovnikBezPDV,
      cenaCenovnikSaPDV: inquiryPrice.cenaCenovnikSaPDV,
      krajnjaCenaBezPDV: inquiryPrice.krajnjaCenaBezPDV,
      krajnjaCenaSaPDV: inquiryPrice.krajnjaCenaSaPDV,
      tarifnaGrupa: tariffGroup,
      tarifnagrupa: tariffGroup,
    };
  }, [product, inquiryPrice, shouldUseRetailPdvPrice, shouldUseWholesaleWithoutPdvPrice]);

  useEffect(() => {
    if ((inCart || selected) && !inquiryPrice) fetchInquiryPrice();
  }, [inCart, selected, inquiryPrice, fetchInquiryPrice, revision]);

  return {
    inquiryPrice,
    fetchInquiryPrice,
    effectiveProduct,
    priceDisplayMode,
    shouldUseRetailPdvPrice,
    shouldUseWholesaleWithoutPdvPrice,
  };
}
